import { Card, CardContent } from '@/components/ui/card.tsx'
import { cn } from '@/lib/utils.ts'
import { useDraggable } from '@dnd-kit/core'
import { Fragment, useMemo } from 'react'

import { BlockerIcon } from '@/components/BlockerIcon.tsx'
import { EstimateIcon } from '@/components/EstimateIcon.tsx'
import { MemberStringList } from '@/components/MemberStringList'
import { StoryTypeIcon } from '@/components/StoryTypeIcon.tsx'
import { withFormToggler } from '@/components/enhancers/withFormToggler.tsx'
import { withSelectable } from '@/components/enhancers/withSelectable.tsx'
import { withSortable } from '@/components/enhancers/withSortable.tsx'
import { StoryLabel } from '@/components/form/StoryLabel.tsx'
import { StatusButton } from '@/components/form/status/StatusButton.tsx'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { getNow } from '@/utils/date.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import { getGroupName } from '@/utils/status.ts'
import { isPointAssignable } from '@/utils/story.ts'

type Props = {
  story: Story
}

export const StoryListElement = ({ story }: Props) => {
  const { setNodeRef, isDragging } = useDraggable({
    id: story.id,
    data: {
      type: story.status,
    },
  })

  const isOverdueReleaseStory = useMemo(
    () =>
      story.storyType === STORY_TYPES.RELEASE &&
      story.status !== STORY_STATUSES.ACCEPTED &&
      story.status !== STORY_STATUSES.UNSCHEDULED &&
      story.deadline !== null &&
      getNow() > story.deadline,
    [story.storyType, story.status, story.deadline],
  )

  const backgroundColor = useMemo(() => {
    switch (true) {
      case isOverdueReleaseStory:
        return 'bg-alarm-red-100'
      case story.storyType === STORY_TYPES.RELEASE:
        return 'bg-surface-releaseMarker-default'
      case story.status === STORY_STATUSES.ACCEPTED:
        return 'bg-surface-neutralMedium'
      default:
        return 'bg-surface-neutralLight'
    }
  }, [story.storyType, story.status, isOverdueReleaseStory])

  return (
    <Card
      id={`story_${story.id}`}
      ref={setNodeRef}
      className={cn('border-0 rounded-none shadow-none cursor-default', { 'opacity-50': isDragging })}
    >
      <CardContent
        className={cn(
          'flex items-start gap-2 p-2 font-EN-KR-body1-regular pr-[30px]',
          'relative hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0 after:pointer-events-none',
          backgroundColor,
        )}
      >
        <div className="h-fit flex flex-col items-center gap-1">
          <div className="h-5 flex items-center justify-center p-0 rounded-xs">
            <StoryTypeIcon
              className="w-5 h-5 p-1 rounded-xs bg-tb-grey-000"
              storyType={story.storyType}
              isAlert={isOverdueReleaseStory}
            />
          </div>
          {story.blockers.length !== 0 && <BlockerIcon size="sm" isBlocked={true} />}
          {(story.blockedStoryIds ?? []).length > 0 && <BlockerIcon size="sm" isBlocked={false} />}
        </div>

        {story.storyType !== STORY_TYPES.RELEASE && (
          <div className="h-5 w-[14px]">
            {isPointAssignable(story.storyType) && (
              <EstimateIcon className="h-5 w-[14px] rounded-sm" estimate={story.estimate} />
            )}
          </div>
        )}

        <div className="flex flex-col items-start flex-1 break-all">
          <div className="text-[14px] leading-5">
            {
              <span
                className={cn('text-contents-primary', {
                  'text-contents-onDarkPrimary': story.storyType === STORY_TYPES.RELEASE,
                })}
              >
                {story.title}
                <MemberStringList targetMembers={story.owners} />
              </span>
            }
          </div>

          <div className="flex flex-wrap items-center">
            {story.labels.map((label, index) => (
              <Fragment key={label.id}>
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: */}
                <div
                  className="bg-transparent text-labelType-label px-0  cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    GlobalPubSub.publish(GlobalPubSub.Signals.SELECT_LABEL, label.name)
                  }}
                >
                  <StoryLabel item={label.name} variant="text" className="font-EN-KR-tag-bold" />
                </div>
                {index < story.labels.length - 1 && (
                  <span className="text-contents-primary font-EN-KR-tag-bold">,&nbsp;</span>
                )}
              </Fragment>
            ))}
          </div>
        </div>
        <StatusButton storyId={story.id} status={story.status} estimate={story.estimate} storyType={story.storyType} />
      </CardContent>
    </Card>
  )
}

export const StoryListElementWithFormToggler = withFormToggler(StoryListElement)
export const SelectableStoryListElement = withSelectable(StoryListElement, {
  getId: (props) => props.story.id,
  getGroupName: (props) => getGroupName(props.story.status).group,
  getStoryType: (props) => props.story.storyType,
})
export const SortableAndSelectableStoryListElement = withSortable(SelectableStoryListElement, {
  getId: (props) => props.story.id,
})
export const SortableAndSelectableStoryListElementWithFormToggler = withFormToggler(
  SortableAndSelectableStoryListElement,
)
